import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import {
  CircularProgress, Box, Skeleton,
  Typography, Button, Divider, List, ListItem, ListItemButton,
  ListItemText, ListItemIcon, Collapse, IconButton, Tooltip,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  InputBase, InputAdornment,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import { injectIntl } from 'react-intl';
import * as componentActions from '../../redux/actions/integrationauths';
import {
  Add as AddIcon,
  ViewKanban as KanbanIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FiberManualRecord as DotIcon,
  Beenhere as BeenhereIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

import TicketKanban, { KanbanSkeleton } from './TicketKanban';
import { paginateBoards, deleteBoard } from '../../services/kanban.service';
import { AbilityContext } from '../../config/ability-context';

const PAGE_SIZE = 10;

// sharemode: PRIVATE=1, USERS=2, TEAMS=3, ALL=4
const GROUPS = [
  { key: 'private', label: 'Meus Quadros',    Icon: LockIcon,   filter: b => b.sharemode === 1 },
  { key: 'public',  label: 'Quadros Públicos', Icon: PublicIcon, filter: b => b.sharemode === 4 },
  { key: 'shared',  label: 'Compartilhados',   Icon: GroupIcon,  filter: b => b.sharemode === 2 || b.sharemode === 3 },
];

const SidebarSkeleton = () => (
  <>
    {[1, 2].map((group) => (
      <Box key={group}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.5 }}>
          <Skeleton variant="circular" width={13} height={13} />
          <Skeleton variant="text" width={90} height={16} sx={{ flex: 1 }} />
          <Skeleton variant="text" width={12} height={16} />
        </Box>
        {[1, 2, 3].map((item) => (
          <Box key={item} sx={{ mx: 0.5, px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={10} height={10} />
            <Skeleton variant="text" width={`${50 + item * 15}%`} height={16} />
          </Box>
        ))}
        <Divider sx={{ mx: 0.5, mt: 0.5 }} />
      </Box>
    ))}
  </>
);

class Kanban extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading:             true,
      loadingMore:         false,
      openNotification:    false,
      notificationVariant: 'error',
      notificationMessage: '',
      boards:              [],
      totalCount:          0,
      page:                1,
      selectedBoardId:     null,
      sidebarOpen:         true,
      deleteModalOpen:     false,
      boardToDelete:       null,
      searchTerm:          '',
      searchActive:        '',
      accordionOpen:       { private: true, public: true, shared: true },
    };

    this.isAdmGlobal = false;
    this.currentUserId = 0;

    const { userSession } = props;
    if (userSession?.user) {
      this.currentUserId = userSession.user.id;
      userSession.user.userrole?.forEach(item => {
        if (item.role.roletype === 0) this.isAdmGlobal = true;
      });
    }

    this.closeNotification   = this.closeNotification.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleDeleteCancel  = this.handleDeleteCancel.bind(this);
    this.handleSearch        = this.handleSearch.bind(this);
    this.handleShowMore      = this.handleShowMore.bind(this);
  }

  async componentDidMount() {
    await this.loadBoards(1, '');
  }

  async loadBoards(page, search, append = false) {
    if (page === 1) {
      this.setState({ loading: !append, loadingMore: append });
    } else {
      this.setState({ loadingMore: true });
    }

    try {
      const res = await paginateBoards(page, PAGE_SIZE, search);
      if (res?.data?.success) {
        const newBoards = res.data.data ?? [];
        const total     = res.data.count ?? 0;

        this.setState(prev => {
          const boards = append ? [...prev.boards, ...newBoards] : newBoards;
          const defaultBoard = !prev.selectedBoardId
            ? (boards.find(b => b.isdefault) ?? boards[0])
            : null;
          return {
            boards,
            totalCount:      total,
            page,
            loading:         false,
            loadingMore:     false,
            selectedBoardId: defaultBoard ? defaultBoard.id : prev.selectedBoardId,
          };
        });
      }
    } catch {
      const intl = this.props.intl;
      this.setState({
        loading:             false,
        loadingMore:         false,
        openNotification:    true,
        notificationVariant: 'error',
        notificationMessage: intl.formatMessage({ id: 'process.error' }),
      });
    }
  }

  handleSearch() {
    const { searchTerm } = this.state;
    this.setState({ searchActive: searchTerm, boards: [], totalCount: 0, page: 1 }, () => {
      this.loadBoards(1, searchTerm);
    });
  }

  async handleShowMore() {
    const { page, searchActive } = this.state;
    await this.loadBoards(page + 1, searchActive, true);
  }

  closeNotification() {
    this.setState({ openNotification: false });
  }

  async handleDeleteConfirm() {
    const { boardToDelete, searchActive, page } = this.state;
    try {
      const res = await deleteBoard(boardToDelete.id);
      if (res?.data?.success) {
        this.setState(prev => {
          const boards = prev.boards.filter(b => b.id !== boardToDelete.id);
          const selectedBoardId = prev.selectedBoardId === boardToDelete.id
            ? (boards[0]?.id ?? null)
            : prev.selectedBoardId;
          return {
            boards,
            totalCount:          prev.totalCount - 1,
            selectedBoardId,
            deleteModalOpen:     false,
            boardToDelete:       null,
            openNotification:    true,
            notificationVariant: 'success',
            notificationMessage: 'Quadro excluído com sucesso.',
          };
        });
      }
    } catch {
      this.setState({
        deleteModalOpen:     false,
        boardToDelete:       null,
        openNotification:    true,
        notificationVariant: 'error',
        notificationMessage: 'Erro ao excluir o quadro.',
      });
    }
  }

  handleDeleteCancel() {
    this.setState({ deleteModalOpen: false, boardToDelete: null });
  }

  getLang() {
    return this.props.match?.params?.lang ?? 'pt';
  }

  canEditOrDelete(board) {
    if (this.isAdmGlobal) return true;
    return board.canedit === true || board.canedit === 1;
  }

  getFilteredBoards() {
    const { boards, searchTerm, searchActive } = this.state;
    // Frontend filter only applies while the user is typing (before pressing Enter)
    if (!searchTerm.trim() || searchTerm === searchActive) return boards;
    const term = searchTerm.toLowerCase();
    return boards.filter(b => b.title.toLowerCase().includes(term));
  }

  toggleAccordion(key) {
    this.setState(prev => ({
      accordionOpen: { ...prev.accordionOpen, [key]: !prev.accordionOpen[key] },
    }));
  }

  renderBoardItem(board) {
    const { selectedBoardId } = this.state;
    const lang = this.getLang();
    const selected = board.id === selectedBoardId;
    const canActOnBoard = this.canEditOrDelete(board);

    return (
      <ListItem key={board.id} disablePadding>
        <ListItemButton
          selected={selected}
          disabled={!board.active}
          onClick={() => this.setState({ selectedBoardId: board.id })}
          sx={{
            borderRadius: 1,
            mx: 0.25,
            px: 0.75,
            position: 'relative',
            overflow: 'hidden',
            '&.Mui-selected': {
              bgcolor: '#e8f0fe',
              '&:hover': { bgcolor: '#dce8fd' },
            },
            '&.Mui-disabled': { opacity: 0.55, pointerEvents: 'none' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 18 }}>
            {board.isdefault
              ? (
                <Tooltip title="Quadro padrão">
                  <BeenhereIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                </Tooltip>
              ) : (
                <DotIcon sx={{ fontSize: 10, color: board.active ? '#10b981' : '#9ca3af' }} />
              )
            }
          </ListItemIcon>
          <Tooltip title={board.title} placement="left" enterDelay={500}>
            <ListItemText
              primary={board.title}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: selected ? 'bold' : 'normal',
                noWrap: true,
              }}
            />
          </Tooltip>
          {canActOnBoard && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                pl: 2,
                pr: 0.5,
                background: selected
                  ? 'linear-gradient(to right, transparent, #e8f0fe 45%)'
                  : 'linear-gradient(to right, transparent, #f8f9fa 45%)',
                opacity: selected ? 1 : 0,
                '.MuiListItem-root:hover &': { opacity: 1 },
                transition: 'opacity 0.15s',
                zIndex: 1,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title="Editar quadro">
                <IconButton
                  size="small"
                  component={Link}
                  to={`/${lang}/kanban-board-edit/${board.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir quadro">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ deleteModalOpen: true, boardToDelete: board });
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    );
  }

  renderGroup(group, groupBoards) {
    const { accordionOpen } = this.state;
    const isOpen = accordionOpen[group.key];
    const { Icon } = group;

    return (
      <Box key={group.key}>
        <Box
          onClick={() => this.toggleAccordion(group.key)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: '#f0f2f5' },
          }}
        >
          <Icon sx={{ fontSize: 13, color: 'text.secondary', mr: 0.5 }} />
          <Typography
            variant="caption"
            fontWeight="bold"
            color="text.secondary"
            sx={{ flex: 1, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
          >
            {group.label}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25, fontSize: '0.65rem' }}>
            {groupBoards.length}
          </Typography>
          {isOpen
            ? <ExpandLessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            : <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          }
        </Box>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {groupBoards.length === 0 ? (
            <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.disabled">
                Sem registros
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {groupBoards.map(board => this.renderBoardItem(board))}
            </List>
          )}
        </Collapse>

        <Divider sx={{ mx: 0.5 }} />
      </Box>
    );
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const {
      loading, loadingMore, selectedBoardId, sidebarOpen,
      openNotification, notificationVariant, notificationMessage,
      deleteModalOpen, boardToDelete, searchTerm, totalCount, boards,
    } = this.state;
    const lang = this.getLang();

    const filteredBoards = this.getFilteredBoards();
    const groups = GROUPS.map(g => ({ ...g, boards: filteredBoards.filter(g.filter) }));
    const hasMore = boards.length < totalCount;

    return (
      <div className={classes.root}>
        <HeaderMenu />
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: headerMenu.open,
          }, classes.actionsContent, {
            [classes.actionscontentShift]: actionsHeaderMenu.open,
          })}
          style={{ paddingTop: '6px', paddingBottom: 0 }}
        >
          <div className={classes.drawerHeader} />

          <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>

            {/* ── Área principal do kanban (esquerda) ── */}
            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {loading ? (
                <KanbanSkeleton />
              ) : selectedBoardId ? (
                <TicketKanban
                  boardId={selectedBoardId}
                  key={selectedBoardId}
                  sidebarOpen={sidebarOpen}
                  onToggleSidebar={() => this.setState((s) => ({ sidebarOpen: !s.sidebarOpen }))}
                  {...this.props}
                />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                  <KanbanIcon sx={{ fontSize: 64, color: '#d1d5db' }} />
                  <Typography variant="h6" color="text.secondary">Nenhum quadro selecionado</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    component={Link}
                    to={`/${lang}/kanban-board-edit`}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Criar primeiro quadro
                  </Button>
                </Box>
              )}
            </Box>

            {/* ── Sidebar de quadros (direita) ── */}
            <Box
              sx={{
                width:         sidebarOpen ? 240 : 0,
                minWidth:      sidebarOpen ? 240 : 0,
                transition:    'width 0.2s, min-width 0.2s',
                overflow:      'hidden',
                bgcolor:       '#f8f9fa',
                borderLeft:    '1px solid #e5e7eb',
                display:       'flex',
                flexDirection: 'column',
                flexShrink:    0,
              }}
            >
              {/* Cabeçalho */}
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <KanbanIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                    Quadros
                  </Typography>
                </Box>
                <Tooltip title="Novo quadro">
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/${lang}/kanban-board-edit`}
                    sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, width: 24, height: 24 }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Divider />

              {/* Busca por título */}
              <Box sx={{ px: 1, py: 0.75 }}>
                <InputBase
                  fullWidth
                  placeholder="Buscar quadro..."
                  value={searchTerm}
                  onChange={(e) => {
                    const val = e.target.value;
                    this.setState({ searchTerm: val }, () => {
                      if (val === '') this.handleSearch();
                    });
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') this.handleSearch(); }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={this.handleSearch} sx={{ p: 0.25 }}>
                        <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                      </IconButton>
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.8rem',
                    '& .MuiInputBase-input': { py: 0.25 },
                  }}
                />
              </Box>

              <Divider />

              {/* Grupos accordion */}
              <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                {loading ? (
                  <SidebarSkeleton />
                ) : (
                  groups.map(g => this.renderGroup(g, g.boards))
                )}

                {!loading && hasMore && (
                  <Box sx={{ px: 1.5, py: 0.75 }}>
                    <Button
                      size="small"
                      fullWidth
                      variant="text"
                      disabled={loadingMore}
                      onClick={this.handleShowMore}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}
                    >
                      {loadingMore
                        ? <CircularProgress size={14} />
                        : `Ver mais (+${totalCount - boards.length})`
                      }
                    </Button>
                  </Box>
                )}
              </Box>


              {totalCount < PAGE_SIZE && (
                <>
                  <Divider />
                  <Box sx={{ p: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      component={Link}
                      to={`/${lang}/kanban-board-edit`}
                      sx={{ textTransform: 'none', borderRadius: 2, borderStyle: 'dashed' }}
                    >
                      Adicionar quadro
                    </Button>
                  </Box>
                </>
              )}
            </Box>

          </Box>

          <Dialog open={deleteModalOpen} onClose={this.handleDeleteCancel}>
            <DialogTitle>Excluir quadro</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Tem certeza que deseja excluir o quadro <strong>{boardToDelete?.title}</strong>? Esta ação não pode ser desfeita.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleDeleteCancel}>Cancelar</Button>
              <Button onClick={this.handleDeleteConfirm} color="error" variant="contained">
                Excluir
              </Button>
            </DialogActions>
          </Dialog>

          <CustomizedSnackbars
            variant={notificationVariant}
            message={notificationMessage}
            isOpen={openNotification}
            toClose={this.closeNotification}
          />
        </main>
        <Footer />
      </div>
    );
  }
}

Kanban.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu:        state.headerMenu,
  userSession:       state.userSession,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(componentActions, dispatch);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(Kanban)));

Kanban.contextType = AbilityContext;
